import apiClient from './client';

// ============================================================
// Types
// ============================================================

export interface ConnectionStatus {
  connected: boolean;
  message: string;
  last_checked?: string;
}

export interface BillComVendorMatch {
  id: string;
  name: string;
  email: string;
  isExactMatch: boolean;
}

export interface VendorMapping {
  billcom_vendor_id: string | null;
  billcom_vendor_name: string | null;
  billcom_linked_at: string | null;
}

export interface UnmappedCoach {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface CoachVendorStatus {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  pool_status: 'assigned' | 'potential' | string;
  billcom_vendor_id: string | null;
  billcom_vendor_name: string | null;
  billcom_linked_at: string | null;
  /** MED-1178: true when this coach has non-voided scheduled payments in the
   *  cohort — used to warn before unlinking/re-mapping their vendor. */
  has_scheduled_payments?: boolean;
}

export type BillComPaymentStatus =
  | 'pending'
  | 'awaiting_approval'
  // MED-1383: approved in Bill.com, no payment scheduled there yet.
  | 'approved'
  | 'scheduled'
  | 'processing'
  | 'paid'
  | 'failed'
  | 'voided';

export interface BillComPayment {
  id: string;
  cohort_id: string;
  coach_user_id: string;
  coach_name?: string;
  comp_result_id: string | null;
  billcom_bill_id: string | null;
  billcom_payment_id: string | null;
  billcom_vendor_id: string;
  payment_track: 'flat_rate_monthly' | 'attendance_dependent_net_10';
  amount_cents: number;
  process_date: string;
  memo: string | null;
  status: BillComPaymentStatus;
  status_updated_at: string | null;
  error_message: string | null;
  /** MED-1383: the date the payment is scheduled in Bill.com, as YYYY-MM-DD. */
  billcom_scheduled_date: string | null;
  /** MED-1383: the Bill.com invoice number. Optional until every API pod sends it. */
  invoice_number?: string | null;
  line_items: Array<{
    group_id: string;
    group_name: string;
    amount_cents: number;
    memo: string;
  }>;
  created_at: string;
}

export interface Net10PendingPayment {
  event_reference_id: string;
  event_reference_type: 'cohort_event' | 'session' | 'coach_debrief_flat_rate';
  event_type: string;
  event_name: string;
  event_date: string;
  /** MED-1383: the event day as YYYY-MM-DD, in the event's own timezone. */
  event_local_date?: string;
  /**
   * MED-1383: the participant of a 1:1 session, null for every other row. Optional
   * because a rolling deploy can serve an older API pod.
   */
  participant_name?: string | null;
  coach_user_id: string;
  coach_name: string;
  coach_first_name: string;
  coach_last_name: string;
  amount_cents: number;
  due_date: string;
  is_overdue: boolean;
  billcom_vendor_id: string | null;
  /** MED-696 AC10/AC11/AC12: drives per-row type labels and grouping. */
  one_on_one_type:
    | 'standard_1_1'
    | 'two_on_one'
    | '360_debrief'
    | 'hogan_debrief'
    | 'other_assessment_debrief'
    | null;
  /**
   * MED-918 AC12 (Option A — "Keep + indicator"): true when the coach was
   * removed from the cohort after attending the event/session this payment
   * is for. The Net-10 ready-to-pay queue keeps the row visible — already-
   * earned attendance shouldn't be revocable by an admin action — and uses
   * this flag to render a "REMOVED" badge with a tooltip next to the coach
   * name so the CM understands why the row is here. False for any payment
   * tied to a coach who is still an active member of the cohort.
   */
  coach_is_removed_from_cohort: boolean;
}

// ============================================================
// Payment Summary & Audit (MED-519 / E13-7)
// ============================================================

/**
 * MED-1383: with nothing paid, the status names the earliest unpaid stage. Before, every
 * unpaid cohort read "Payments Scheduled", even with no bill created yet.
 */
export type AggregatePaymentStatus =
  | 'payments_pending'
  | 'awaiting_approval'
  | 'approved'
  | 'payments_scheduled'
  | 'payments_failed'
  | 'partially_paid'
  | 'fully_paid';

/** MED-1383: what Schedule Payments will create. The confirm dialog shows it. */
export interface SchedulePaymentsPreview {
  total_payments: number;
  total_cents: number;
  coach_count: number;
  program_months: number;
  first_payment_date: string | null;
  /**
   * The date of the last bill, computed as the run computes it. Optional because a
   * rolling deploy can serve an older API pod.
   */
  last_payment_date?: string | null;
  cohort_end_date?: string | null;
  /** True when the last bill falls more than one month after the cohort end month. */
  runs_past_cohort_end?: boolean;
  unmapped_coach_count: number;
}

export interface CohortPaymentSummary {
  /** MED-1286: excludes voided (not owed), so Paid + Remaining = Total Confirmed. */
  total_confirmed_cents: number;
  total_paid_cents: number;
  /** MED-1286: everything not paid and not voided — failed included. */
  total_remaining_cents: number;
  /** MED-1286 (AC3): the failed slice of total_remaining_cents. */
  total_failed_cents: number;
  /** MED-1286: excluded from confirmed/remaining; for display only. */
  total_voided_cents: number;
  next_payment_date: string | null;
  counts: {
    pending: number;
    /** MED-1306 / MDF-174: bill created, no human approval yet. */
    awaiting_approval: number;
    /** MED-1383: optional because a rolling deploy can serve an older API pod. */
    approved?: number;
    scheduled: number;
    processing: number;
    paid: number;
    failed: number;
    voided: number;
  };
  aggregate_status: AggregatePaymentStatus | null;
}

export interface PaymentAuditEntry {
  id: string;
  payment_id: string;
  cohort_id: string;
  coach_user_id: string;
  previous_status: string | null;
  new_status: string;
  source: 'webhook' | 'polling' | 'manual' | 'system';
  billcom_event_type: string | null;
  created_at: string;
}

export interface PaymentFilters {
  status?: string[];
  payment_track?: string[];
  coach_user_id?: string;
}

export interface PaymentJob {
  id: string;
  cohort_id: string;
  initiated_by: string;
  total_payments: number;
  completed_count: number;
  failed_count: number;
  status: string;
  errors: Array<{
    payment_id: string;
    coach_user_id: string;
    error: string;
    timestamp: string;
  }>;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

// ============================================================
// Admin Bill.com API
// ============================================================

export const billcomApi = {
  // Connection
  getStatus: () => apiClient.get<ConnectionStatus>('/api/admin/billcom/status'),

  testConnection: () =>
    apiClient.post<ConnectionStatus>('/api/admin/billcom/test-connection'),

  // Vendor search. `refresh` busts the 15-min server-side vendor cache (MED-1174).
  searchVendors: (q: string, coachEmail?: string, refresh?: boolean) => {
    const params = new URLSearchParams({ q });
    if (coachEmail) params.set('coach_email', coachEmail);
    if (refresh) params.set('refresh', 'true');
    return apiClient.get<BillComVendorMatch[]>(
      `/api/admin/billcom/vendors/search?${params}`,
    );
  },

  getSuggestedMatches: (coachUserId: string, refresh?: boolean) =>
    apiClient.get<BillComVendorMatch[]>(
      `/api/admin/billcom/coaches/${coachUserId}/vendor/suggestions${
        refresh ? '?refresh=true' : ''
      }`,
    ),

  // Vendor mapping
  getVendorMapping: (coachUserId: string) =>
    apiClient.get<VendorMapping>(
      `/api/admin/billcom/coaches/${coachUserId}/vendor`,
    ),

  linkVendor: (coachUserId: string, vendorId: string, vendorName: string) =>
    apiClient.post<VendorMapping>(
      `/api/admin/billcom/coaches/${coachUserId}/vendor`,
      { vendor_id: vendorId, vendor_name: vendorName },
    ),

  unlinkVendor: (coachUserId: string) =>
    apiClient.delete(`/api/admin/billcom/coaches/${coachUserId}/vendor`),
};

// ============================================================
// Cohort Bill.com API
// ============================================================

export const cohortBillcomApi = {
  // Coach vendor status
  getCoachVendorStatus: (cohortId: string) =>
    apiClient.get<CoachVendorStatus[]>(
      `/api/cohorts/${cohortId}/billcom/coaches`,
    ),

  getUnmappedCoaches: (cohortId: string) =>
    apiClient.get<UnmappedCoach[]>(
      `/api/cohorts/${cohortId}/billcom/coaches/unmapped`,
    ),

  // Payments (flat-rate) with optional filters (AC10)
  getPayments: (cohortId: string, filters?: PaymentFilters) => {
    const params = new URLSearchParams();
    if (filters?.status?.length) params.set('status', filters.status.join(','));
    if (filters?.payment_track?.length)
      params.set('payment_track', filters.payment_track.join(','));
    if (filters?.coach_user_id) params.set('coach_user_id', filters.coach_user_id);
    const qs = params.toString();
    return apiClient.get<BillComPayment[]>(
      `/api/cohorts/${cohortId}/billcom/payments${qs ? `?${qs}` : ''}`,
    );
  },

  schedulePayments: (cohortId: string) =>
    apiClient.post<{ job_id: string; total_payments: number }>(
      `/api/cohorts/${cohortId}/billcom/payments/schedule`,
    ),

  getSchedulePreview: (cohortId: string) =>
    apiClient.get<SchedulePaymentsPreview>(
      `/api/cohorts/${cohortId}/billcom/payments/schedule/preview`,
    ),

  voidCoachPayments: (cohortId: string, coachUserId: string) =>
    apiClient.post<{ voided_count: number }>(
      `/api/cohorts/${cohortId}/billcom/payments/coaches/${coachUserId}/void`,
    ),

  // Net-10 payments (attendance-dependent)
  getNet10PendingPayments: (cohortId: string) =>
    apiClient.get<Net10PendingPayment[]>(
      `/api/cohorts/${cohortId}/billcom/payments/net10/pending`,
    ),

  getNet10SubmittedPayments: (cohortId: string) =>
    apiClient.get<BillComPayment[]>(
      `/api/cohorts/${cohortId}/billcom/payments/net10/submitted`,
    ),

  submitNet10Payment: (
    cohortId: string,
    data: {
      event_reference_id: string;
      event_reference_type: 'cohort_event' | 'session' | 'coach_debrief_flat_rate';
      coach_user_id: string;
    },
  ) =>
    apiClient.post<{ payment_id: string; billcom_bill_id: string; billcom_payment_id: string }>(
      `/api/cohorts/${cohortId}/billcom/payments/net10/submit`,
      data,
    ),

  submitAllNet10Payments: (cohortId: string) =>
    apiClient.post<{ job_id: string; total_payments: number }>(
      `/api/cohorts/${cohortId}/billcom/payments/net10/submit-all`,
    ),

  // Payment summary (AC11)
  getPaymentSummary: (cohortId: string) =>
    apiClient.get<CohortPaymentSummary>(
      `/api/cohorts/${cohortId}/billcom/payments/summary`,
    ),

  // Payment audit trail (AC19)
  getPaymentAuditTrail: (cohortId: string, paymentId: string) =>
    apiClient.get<PaymentAuditEntry[]>(
      `/api/cohorts/${cohortId}/billcom/payments/${paymentId}/audit`,
    ),

  // Retry failed payment (AC12)
  retryFailedPayment: (cohortId: string, paymentId: string) =>
    apiClient.post<{ payment_id: string; status: string }>(
      `/api/cohorts/${cohortId}/billcom/payments/${paymentId}/retry`,
    ),

  // Jobs
  getLatestJob: (
    cohortId: string,
    paymentTrack: BillComPayment['payment_track'] = 'flat_rate_monthly',
  ) =>
    apiClient.get<PaymentJob | null>(
      `/api/cohorts/${cohortId}/billcom/jobs/latest?payment_track=${paymentTrack}`,
    ),

  getJobProgress: (cohortId: string, jobId: string) =>
    apiClient.get<PaymentJob>(
      `/api/cohorts/${cohortId}/billcom/jobs/${jobId}`,
    ),
};
